/**
 * DRM Management console — shared status-tone mappers + formatters.
 * Mirrors healthcheck/shared.tsx conventions (caller maps domain status → tone;
 * StatusPill stays free of hardcoded statuses).
 */
import type { StreamKind, StreamStatus } from '@/api/aes/streams';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

/** Stream lifecycle → StatusPill tone. */
export function streamStatusTone(status: StreamStatus): Tone {
  switch (status) {
    case 'live':
      return 'success';
    case 'provisioning':
      return 'warning';
    case 'pending':
      return 'info';
    case 'stopped':
      return 'muted';
    default:
      return 'neutral';
  }
}

export const STREAM_STATUS_LABEL: Record<StreamStatus, string> = {
  live: 'Live',
  provisioning: 'Provisioning',
  pending: 'Pending',
  stopped: 'Stopped',
};

/** LIVE vs VOD → Badge tone (primary = live edge, accent = on-demand). */
export function kindTone(kind: StreamKind): 'primary' | 'accent' {
  return kind === 'live' ? 'primary' : 'accent';
}

export const KIND_LABEL: Record<StreamKind, string> = {
  live: 'LIVE',
  vod: 'VOD',
};

/** mm:ss countdown for the rotation view. */
export function fmtCountdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

/** "2.8 Mbps" from bits/s. */
export function fmtBitrate(bps: number | undefined): string {
  if (!bps || bps <= 0) return '—';
  return `${(bps / 1_000_000).toFixed(1)} Mbps`;
}

/** "1h 30m" / "45s" from seconds (DVR window, period). */
export function fmtDuration(seconds: number | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export function fmtInt(n: number | undefined): string {
  return typeof n === 'number' ? n.toLocaleString() : '—';
}
